import React from "react"

const DEFAULT_MIN_WIDTH = 320
const DEFAULT_MAX_WIDTH = Number.POSITIVE_INFINITY
const DEFAULT_VIEWPORT_PADDING = 0

type ResizablePanelConfig = {
  defaultWidth?: number
  minWidth?: number
  maxWidth?: number
  localStorageKey?: string
  viewportPadding?: number
  // Docked edge; flips which drag direction grows the panel.
  anchor?: "left" | "right"
}

type ClampResizablePanelWidthArgs = {
  requestedWidth: number
  minWidth: number
  maxWidth: number
  viewportPadding: number
  viewportWidth?: number
}

type ResizeState = {
  startX: number
  startWidth: number
}

type ResizeStartEvent = {
  button?: number
  clientX: number
  preventDefault: () => void
}

const clampResizablePanelWidth = ({
  requestedWidth,
  minWidth,
  maxWidth,
  viewportPadding,
  viewportWidth = window.innerWidth,
}: ClampResizablePanelWidthArgs) => {
  const availableWidth = Math.max(viewportWidth - viewportPadding, 0)
  const constrainedMaxWidth = Math.min(maxWidth, availableWidth)
  const constrainedMinWidth = Math.min(minWidth, constrainedMaxWidth)

  return Math.min(Math.max(requestedWidth, constrainedMinWidth), constrainedMaxWidth)
}

const loadStoredPanelWidth = (localStorageKey?: string) => {
  if (!localStorageKey) {
    return null
  }

  try {
    const storedValue = window.localStorage.getItem(localStorageKey)

    if (storedValue === null) {
      return null
    }

    const parsedValue = Number(JSON.parse(storedValue))
    return Number.isFinite(parsedValue) ? parsedValue : null
  } catch (error) {
    console.warn("Failed to load panel width from localStorage:", error)
    return null
  }
}

const saveStoredPanelWidth = (localStorageKey: string | undefined, width: number) => {
  if (!localStorageKey) {
    return
  }

  try {
    window.localStorage.setItem(localStorageKey, JSON.stringify(width))
  } catch (error) {
    console.warn("Failed to save panel width to localStorage:", error)
  }
}

export const useResizablePanel = (
  ref: React.RefObject<HTMLElement | null>,
  {
    defaultWidth = DEFAULT_MIN_WIDTH,
    minWidth = DEFAULT_MIN_WIDTH,
    maxWidth = DEFAULT_MAX_WIDTH,
    localStorageKey,
    viewportPadding = DEFAULT_VIEWPORT_PADDING,
    anchor = "right",
  }: ResizablePanelConfig = {},
) => {
  const resizeStateRef = React.useRef<ResizeState | null>(null)
  const [isResizing, setIsResizing] = React.useState(false)
  const [viewportWidth, setViewportWidth] = React.useState(() => window.innerWidth)
  const [preferredWidth, setPreferredWidth] = React.useState(
    () => loadStoredPanelWidth(localStorageKey) ?? defaultWidth,
  )
  const preferredWidthRef = React.useRef(preferredWidth)

  const width = React.useMemo(
    () =>
      clampResizablePanelWidth({
        requestedWidth: preferredWidth,
        minWidth,
        maxWidth,
        viewportPadding,
        viewportWidth,
      }),
    [maxWidth, minWidth, preferredWidth, viewportPadding, viewportWidth],
  )

  React.useEffect(() => {
    const handleWindowResize = () => {
      setViewportWidth(window.innerWidth)
    }

    handleWindowResize()
    window.addEventListener("resize", handleWindowResize)

    return () => {
      window.removeEventListener("resize", handleWindowResize)
    }
  }, [])

  React.useEffect(() => {
    if (!isResizing) {
      return undefined
    }

    const handlePointerMove = (event: { clientX: number }) => {
      if (!resizeStateRef.current) {
        return
      }

      const { startX, startWidth } = resizeStateRef.current
      const deltaX = anchor === "right" ? startX - event.clientX : event.clientX - startX
      const nextWidth = clampResizablePanelWidth({
        requestedWidth: startWidth + deltaX,
        minWidth,
        maxWidth,
        viewportPadding,
      })

      preferredWidthRef.current = nextWidth
      setPreferredWidth(nextWidth)
    }

    const stopResizing = () => {
      if (!resizeStateRef.current) {
        return
      }

      resizeStateRef.current = null
      setIsResizing(false)
      saveStoredPanelWidth(localStorageKey, preferredWidthRef.current)
    }

    window.addEventListener("pointermove", handlePointerMove)
    window.addEventListener("pointerup", stopResizing)
    window.addEventListener("pointercancel", stopResizing)

    return () => {
      window.removeEventListener("pointermove", handlePointerMove)
      window.removeEventListener("pointerup", stopResizing)
      window.removeEventListener("pointercancel", stopResizing)
    }
  }, [anchor, isResizing, localStorageKey, maxWidth, minWidth, viewportPadding])

  React.useEffect(() => {
    if (!isResizing) {
      return undefined
    }

    const previousCursor = document.body.style.cursor
    const previousUserSelect = document.body.style.userSelect

    document.body.style.cursor = "col-resize"
    document.body.style.userSelect = "none"

    return () => {
      document.body.style.cursor = previousCursor
      document.body.style.userSelect = previousUserSelect
    }
  }, [isResizing])

  const handleResizeStart = React.useCallback(
    (event: ResizeStartEvent, startWidth?: number) => {
      if (!ref.current) {
        return
      }

      if (typeof event.button === "number" && event.button !== 0) {
        return
      }

      event.preventDefault()

      const resolvedStartWidth = startWidth ?? width
      preferredWidthRef.current = resolvedStartWidth
      resizeStateRef.current = {
        startX: event.clientX,
        startWidth: resolvedStartWidth,
      }

      setIsResizing(true)
    },
    [ref, width],
  )

  return {
    width,
    isResizing,
    handleResizeStart,
  }
}
